import { inject, singleton } from "tsyringe";
import { App, Component, ComponentOptionsBase, ComponentPublicInstance, ComputedOptions, createApp, MethodOptions } from "vue";
import { ExtensionContext } from "../../common/models/extension-config";
import { Module } from "../../common/module";
import { CommonModule } from "../common.module";

@singleton()
export class UiModule extends Module {
    // This module is responsible for injecting custom styles and render Vue UI components on the Tableau Interface
    // NOTE: only use this module to inject Vue Components (do not call createApp() manually) to ensure that corresponding styles/fonts are also inserted by this module
    public name: string = 'UiModule';
    public acceptedLicenseFlags: string[] = [];
    // does not run on its own
    public shouldRun(context: ExtensionContext): boolean { return false; }
    // defined constant names
    public readonly mainFontName = 'Benton Sans STM';
    public readonly cssVariables: { [name: string]: string } = {
        // the font used everywhere
        '--stm-primary-font': `"${this.mainFontName}", "Benton Sans", Arial, Helvetica, sans-serif`,
        '--stm-font-size': '12px',
        // the default font color (on white)
        '--stm-text-color': 'rgba(0, 0, 0, 0.8)',
        '--stm-text-strong-color': 'rgba(0, 0, 0, 1)',
        '--stm-link-color': 'rgb(26, 105, 158)',
        // the default highlight color
        '--stm-primary-color': 'rgb(26, 105, 158)',
        // the default highlight color for primary
        '--stm-primary-highlight-color': 'rgb(42, 121, 175)',
        // the default text color on primary
        '--stm-on-primary-color': 'rgb(255,255,255)',
        // the default secondary color (white)
        '--stm-secondary-color': 'rgba(255,255,255,1)',
        // the default secondary highlight color
        '--stm-secondary-highlight-color': 'rgb(250, 250, 250)',
        '--stm-error-color': 'red',
        '--stm-success-color': 'green',
        // the border color for controls (when not disabled)
        '--stm-control-border-color': '#cbcbcb',

        // the default disabled color
        '--stm-disabled-color': 'rgb(205, 205, 205)',
        // the default color for gray text
        '--stm-gray-text-color': '#919191',

        '--stm-list-item-highlight-color': 'rgb(230, 230, 230)'

      
        

    };


    constructor(@inject(CommonModule) private commonModule: CommonModule) {
        super([commonModule]);
    }


    public injectUI(vueComponent: Component<any, any, any, ComputedOptions, MethodOptions>
        , targetElement?: HTMLElement, vueProps?: Record<string, unknown>): App<Element> {
        const app = createApp(vueComponent, vueProps);
        if (!targetElement) {
            targetElement = document.createElement('div');
            document.body.appendChild(targetElement);
        }
        app.mount(targetElement);
        return app;
    }

    public async run(): Promise<void> {
        // inject CSS variables
        const rootStyle = document.documentElement.style;
        Object.keys(this.cssVariables).forEach(k => rootStyle.setProperty(k, this.cssVariables[k]));

        // inject styles in parallel
        
        await Promise.all([
            this.injectStyle(this.mainFontName, {
                weight: 'normal',
                style: 'normal'
            }, 'img/BentonSans-Book.woff'),
            this.injectStyle(this.mainFontName, {
                weight: 'normal',
                style: 'italic'
            }, 'img/BentonSans-Book_Italic.woff'),
            this.injectStyle(this.mainFontName, {
                weight: 'bold',
                style: 'normal'
            }, 'img/BentonSans-Medium.woff')]);

    }

    private injectStyle(name: string, desc: FontFaceDescriptors, woffRelUrl: string): Promise<void> {
        const woffUrl = this.commonModule.combineUrl(window.location.origin, `${woffRelUrl}?stm`);
        const woff2Url = this.commonModule.combineUrl(window.location.origin, `${woffRelUrl}2?stm`);
        const src = `url(${woff2Url}) format('woff2'), url(${woffUrl}) format('woff')`
        return new FontFace(name, src, desc).load().then(f => { document.fonts.add(f); return undefined; }).catch(e => this.log.w(`Failed to load STM font`, e));
    }



}